import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../components/AuthProvider';
import { normalizeAuthReturnPath, saveAuthReturnPath } from '../../../lib/authReturnPath';

const VOID_BG = '#080810';
const CARD_BG = '#0D0D1A';
const CYAN = '#00FFE5';
const ICE = '#E8F4FF';
const MUTED = '#8899AA';
const ERROR = '#FF4444';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const LoginForm = ({ onLogin, isLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithOAuth, resetPasswordForEmail } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (notice) setNotice('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'EMAIL REQUIRED';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'INVALID EMAIL FORMAT';
    if (!formData?.password) newErrors.password = 'PASSWORD REQUIRED';
    else if (formData?.password?.length < 6) newErrors.password = 'MINIMUM 6 CHARACTERS';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    if (onLogin) onLogin({ email: formData?.email, password: formData?.password, rememberMe: formData?.rememberMe });
  };

  const handleSocialLogin = async (provider) => {
    saveAuthReturnPath(normalizeAuthReturnPath(location.state?.from, '/operations'));
    const { error } = await signInWithOAuth(provider, {
      redirectTo: `${window.location.origin}/user-login`,
    });
    if (error) setErrors(prev => ({ ...prev, general: error.message }));
  };

  const handlePasswordReset = async () => {
    setNotice('');
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'ENTER A VALID EMAIL FIRST' }));
      return;
    }
    const { error } = await resetPasswordForEmail(formData.email, {
      redirectTo: `${window.location.origin}/user-login`,
    });
    if (error) {
      setErrors(prev => ({ ...prev, general: error.message }));
      return;
    }

    setErrors(prev => ({ ...prev, general: '' }));
    setNotice('PASSWORD RESET EMAIL SENT');
  };

  const inputStyle = {
    width: '100%',
    background: '#0A0A14',
    border: '1px solid rgba(0,255,229,0.3)',
    color: ICE,
    padding: '10px 14px',
    fontFamily: MONO,
    fontSize: '0.82rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    color: CYAN,
    fontFamily: MONO,
    fontSize: '0.65rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginBottom: '6px',
    fontWeight: 700,
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h1 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1.8rem', color: ICE, marginBottom: '0.5rem' }}>
          SYSTEM ACCESS
        </h1>
        <p style={{ color: MUTED, fontFamily: MONO, fontSize: '0.75rem' }}>
          Authenticate to continue your mission
        </p>
      </div>
      {/* Social Login */}
      <div className="space-y-3 mb-6">
        {[
          { provider: 'google', label: 'CONTINUE WITH GOOGLE', icon: 'Chrome' },
        ]?.map(item => (
          <button key={item?.provider} onClick={() => handleSocialLogin(item?.provider)} disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 py-3 transition-all"
            style={{ background: 'transparent', border: '1px solid rgba(0,255,229,0.3)', color: CYAN, fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = CYAN}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,255,229,0.3)'}
          >
            <Icon name={item?.icon} size={16} />
            {item?.label}
          </button>
        ))}
      </div>
      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full" style={{ borderTop: '1px solid rgba(0,255,229,0.15)' }}></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span style={{ background: VOID_BG, padding: '0 12px', color: MUTED, fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.1em' }}>OR AUTHENTICATE DIRECTLY</span>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {errors?.general && (
          <div className="p-3" style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.3)' }}>
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} style={{ color: ERROR }} />
              <p style={{ fontSize: '0.75rem', color: ERROR, fontFamily: MONO }}>{errors?.general}</p>
            </div>
          </div>
        )}

        {notice && (
          <div className="p-3" style={{ background: 'rgba(45,200,150,0.1)', border: '1px solid rgba(45,200,150,0.35)' }}>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} style={{ color: '#2DC896' }} />
              <p style={{ fontSize: '0.75rem', color: '#2DC896', fontFamily: MONO }}>{notice}</p>
            </div>
          </div>
        )}

        <div>
          <label style={labelStyle}>Email Address</label>
          <input
            type="email" name="email" placeholder="commander@domain.com"
            value={formData?.email} onChange={handleInputChange} disabled={isLoading}
            style={{ ...inputStyle, borderColor: errors?.email ? ERROR : 'rgba(0,255,229,0.3)' }}
            onFocus={e => e.target.style.borderColor = CYAN}
            onBlur={e => e.target.style.borderColor = errors?.email ? ERROR : 'rgba(0,255,229,0.3)'}
          />
          {errors?.email && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{errors?.email}</p>}
        </div>

        <div>
          <label style={labelStyle}>Password</label>
          <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'} name="password" placeholder="••••••••"
            value={formData?.password} onChange={handleInputChange} disabled={isLoading}
            style={{ ...inputStyle, borderColor: errors?.password ? ERROR : 'rgba(0,255,229,0.3)', paddingRight: '44px' }}
            onFocus={e => e.target.style.borderColor = CYAN}
            onBlur={e => e.target.style.borderColor = errors?.password ? ERROR : 'rgba(0,255,229,0.3)'}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={isLoading}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
          </div>
          {errors?.password && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{errors?.password}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input type="checkbox" name="rememberMe" checked={formData?.rememberMe} onChange={handleInputChange} disabled={isLoading}
              style={{ accentColor: CYAN }} />
            <span style={{ fontSize: '0.72rem', color: MUTED, fontFamily: MONO }}>REMEMBER SESSION</span>
          </label>
          <button type="button" onClick={handlePasswordReset} disabled={isLoading}
            style={{ fontSize: '0.72rem', color: CYAN, fontFamily: MONO, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            RESET ACCESS
          </button>
        </div>

        <button type="submit" disabled={isLoading}
          style={{ background: 'rgba(45, 200, 150, 0.15)', border: '1px solid #2DC896', color: '#2DC896', width: '100%', padding: '12px 24px', textTransform: 'uppercase', letterSpacing: '0.1em', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontFamily: 'inherit', opacity: isLoading ? 0.7 : 1 }}>
{isLoading ? '[ AUTHENTICATING... ]' : '[ AUTHENTICATE ]'}
        </button>
      </form>
      <div className="mt-6 text-center">
        <p style={{ fontSize: '0.75rem', color: MUTED, fontFamily: MONO }}>
          No account?{' '}
          <button onClick={() => navigate('/user-registration')} disabled={isLoading}
            style={{ color: CYAN, fontFamily: MONO, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.08em' }}>
            SIGN UP NOW
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
